import { Typography, useTheme } from '@mui/material';
import { type DefaultWeightUnit } from '@strength/db-schema';
import {
  FULL_DENIED_PERMISSIONS,
  getPermissionsForLegacyRole,
  isPermissionDrivenRole,
  isPermissionGranted,
  permissionValue,
  TeamPreview,
  type PermissionAction,
  type PermissionGroupId,
  type ProfilePermissions,
  type ProfileRoleType,
} from '@strength/domain';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { z } from 'zod';
import { useAuth } from '../../auth';
import { environment } from '../../environment';
import {
  ReportingUser,
  setReportingUserForAdmin,
  setReportingUserForProfile,
} from '../../reporting/reporting';
import { trpc } from '../../trpc/react';
import { MFELoading } from '../components/loading/MFELoading';
import { useFlags } from './featureFlag';
import { deriveHubConnected } from './hubConnected';

const zUUID = z.uuid();

export const isSelectedTeamValid = (
  selectedTeam: string,
  teams: TeamPreview[],
) => {
  return selectedTeam && teams.some((t) => t.id === selectedTeam);
};

// The `tw-team-ids` request header (apps/web/src/trpc/common.ts
// getTeamIdsHeader) is derived solely from the URL's first path segment.
// Any query whose team scope is conveyed only via that header (e.g.
// admin.axleTeamDocument) must gate on this same URL-derived value, not on
// the sticky `selectedTeam` state below: otherwise navigating to a
// team-agnostic route (e.g. /messaging) still "looks" team-scoped from a
// prior page, but no header is actually sent, and the server 400s.
export const getUrlTeam = (pathname: string): string => {
  const firstSegment = pathname.split('/')[1] ?? '';
  return zUUID.safeParse(firstSegment).success ? firstSegment : '';
};

/**
 * SC-4768: resolve the viewer's permission document, failing closed.
 *
 * A permission-driven coach gets the server-resolved document, but only
 * while the myPermissions query is healthy: before it loads (no doc) or
 * after it fails — including a failed refetch, where TanStack Query keeps
 * reporting the last successful `data` — the viewer resolves to the
 * fully-denied document so gated UI renders disabled rather than
 * clickable-and-failing server-side. Legacy roles never consult a document.
 */
export const resolveViewerPermissions = ({
  isPermissionDriven,
  legacyRole,
  permissionsDoc,
  queryFailed,
}: {
  isPermissionDriven: boolean;
  legacyRole: ProfileRoleType | undefined;
  permissionsDoc: ProfilePermissions | undefined;
  queryFailed: boolean;
}): ProfilePermissions => {
  if (!isPermissionDriven) {
    return getPermissionsForLegacyRole(legacyRole);
  }
  return queryFailed || !permissionsDoc
    ? FULL_DENIED_PERMISSIONS
    : permissionsDoc;
};

type ViewerContext = {
  org: {
    orgId: string;
    orgName: string;
    hasDigitalDisplays: boolean;
    settings: {
      enableTeamLibrary: boolean;
      requireExerciseMetadataFields: boolean;
      showBaseLibrary: boolean;
      defaultWeightUnit: DefaultWeightUnit;
    };
  };
  profile: {
    role?: ProfileRoleType; // Optional - only set for org users, not admins
    permissions: ProfilePermissions;
    hasAmsDashboardAccess: boolean;
  };
  navbar: {
    appTargetUrls?: Record<string, string>;
    navbarBottomColor: string;
    navbarTopColor: string;
    navbarImageUrl: string;
    canManageAppAccess: boolean;
  };
  reportingUser: ReportingUser;
  hubConnected: boolean;
  hasPermissions: (
    group: PermissionGroupId,
    action: PermissionAction,
  ) => boolean;
  team: {
    selectedTeam: string;
    setSelectedTeam: (teamId: string) => void;
    teams: TeamPreview[];
  };
};

const ViewerContext = createContext<ViewerContext | null>(null);

export const ViewerProvider = ({
  selectedTeam,
  setSelectedTeam,
  children,
}: {
  selectedTeam: string;
  setSelectedTeam: (teamId: string) => void;
  children: ReactNode;
}) => {
  const { auth } = useAuth();
  const { digitalDisplaysFlag } = useFlags();
  const {
    palette: {
      products: { performance },
      common: { black },
    },
  } = useTheme();

  const { data: teamsData } = trpc.roster.teamsPreview.useQuery(undefined, {
    enabled: !!auth && auth.type === 'org', // Only fetch for org users, not admin users
  });
  const { data: reportingUserData } = trpc.user.reportingUser.useQuery(
    undefined,
    {
      enabled: !!auth,
    },
  );
  const user = reportingUserData?.user;
  const profile = reportingUserData?.profile;

  const { data: orgDocumentData } = trpc.admin.axleOrgDocument.useQuery(
    { orgId: auth?.type === 'org' ? auth?.orgId : '' },
    {
      enabled: auth?.type === 'org',
    },
  );

  const urlTeam = getUrlTeam(window.location.pathname);
  const effectiveTeam = selectedTeam || urlTeam;

  const { data: teamDocumentData } = trpc.admin.axleTeamDocument.useQuery(
    undefined,
    {
      // Gate on urlTeam, not effectiveTeam: the tw-team-ids header this
      // query relies on is URL-derived only, so enablement must agree with
      // it. A stale `selectedTeam` from a previous team page must not
      // enable this query on team-agnostic routes like /messaging.
      enabled:
        urlTeam !== '' &&
        !!isSelectedTeamValid(urlTeam, teamsData ?? []) &&
        auth?.type === 'org',
    },
  );

  const { data: personDocumentData } = trpc.admin.axlePersonDocument.useQuery(
    {
      profileId: auth?.type === 'org' ? auth?.profileId : '',
      orgId: auth?.type === 'org' ? auth?.orgId : '',
    },
    {
      enabled: auth?.type === 'org',
    },
  );

  const { data: orgSettingsData } = trpc.orgSettings.orgDetail.useQuery(
    undefined,
    {
      enabled: auth?.type === 'org',
    },
  );

  // The viewer's permission document. Only permission-driven roles
  // (coach / coach-admin) are governed by a document; every pre-existing
  // role resolves through the legacy coreLibrary split (read-only library
  // unless all-access / team-coach), flag on or off, until it is migrated.
  const isPermissionDriven =
    !!auth && auth.type === 'org' && isPermissionDrivenRole(auth.role);
  const {
    data: myPermissionsData,
    isError: myPermissionsFailed,
    // isInitialLoading, NOT isLoading: on TanStack Query v4, isLoading is
    // `status === 'loading'`, which is true forever for a disabled query that
    // never fetched (legacy/admin sessions, logged-out viewers) — gating on it
    // strands those users on an infinite spinner. isInitialLoading
    // (isLoading && isFetching) is true only while a permission-driven user's
    // first fetch is actually in flight, and false for disabled queries.
    isInitialLoading: myPermissionsLoading,
  } = trpc.permissions.myPermissions.useQuery(undefined, {
    enabled: isPermissionDriven,
  });
  // Memoized by resolved inputs: getPermissionsForLegacyRole returns a fresh
  // object per call, so without this every provider render recreates the
  // context value and re-renders all useViewer consumers.
  const legacyRole = auth?.type === 'org' ? auth.role : undefined;
  // SC-4768: see resolveViewerPermissions — the fully-denied document applies
  // while the query is loading or failed; the success path is unchanged.
  const permissions: ProfilePermissions = useMemo(
    () =>
      resolveViewerPermissions({
        isPermissionDriven,
        legacyRole,
        permissionsDoc: myPermissionsData?.permissions,
        queryFailed: myPermissionsFailed,
      }),
    [
      isPermissionDriven,
      legacyRole,
      myPermissionsData?.permissions,
      myPermissionsFailed,
    ],
  );

  const appTargetUrls = useMemo(() => {
    const webAccess = personDocumentData?._document.login_access?.web ?? {};
    const apps = Object.entries(webAccess)
      .filter(([, hasAccess]) => hasAccess === true)
      .reduce<Record<string, string>>((acc, [app]) => {
        const targetApp = orgDocumentData?.appTargetUrls?.find(
          (t) => t?.appName === app,
        );
        if (app && targetApp && targetApp.targetUrl) {
          acc[app] = targetApp.targetUrl;
        }
        return acc;
      }, {});
    return apps;
  }, [
    orgDocumentData?.appTargetUrls,
    personDocumentData?._document.login_access?.web,
  ]);

  // Create reporting user object
  const reportingUser: ReportingUser = useMemo(
    () => ({
      email: profile?.email ?? '',
      givenName: profile?.givenName ?? '',
      familyName: profile?.familyName ?? '',
      isActive: user?.isActive ?? false,
      isAdmin: user?.isAdmin ?? false,
      axleProfileId: profile?.axleProfileId ?? '',
      axleOrgId: profile?.axleOrgId ?? '',
    }),
    [profile, user],
  );

  const hasPermissions = useCallback(
    (group: PermissionGroupId, action: PermissionAction) =>
      isPermissionGranted(permissionValue(permissions, group, action)),
    [permissions],
  );

  const value = useMemo(() => {
    return {
      org: {
        orgId: auth?.type === 'org' ? auth?.orgId : '',
        orgName:
          orgDocumentData?.org.name ??
          (auth?.type === 'org' ? auth?.orgName : ''),
        hasDigitalDisplays:
          digitalDisplaysFlag &&
          orgDocumentData?._document.app_status?.tv === 'active',
        settings: {
          enableTeamLibrary:
            orgSettingsData?.settings.enableTeamLibrary ?? false,
          requireExerciseMetadataFields:
            orgSettingsData?.settings.requireExerciseMetadataFields ?? false,
          showBaseLibrary: orgSettingsData?.settings.showBaseLibrary ?? false,
          defaultWeightUnit:
            orgSettingsData?.settings.defaultWeightUnit ?? 'lbs',
        },
      },
      profile: {
        // Role is only set for org users (from auth session), not admins
        ...(auth?.type === 'org' && { role: auth.role }),
        permissions,
        hasAmsDashboardAccess: !!(
          personDocumentData?._document.login_access?.web?.ams ||
          personDocumentData?._document.login_access?.web?.hp
        ),
      },
      team: {
        selectedTeam: effectiveTeam,
        setSelectedTeam,
        teams: teamsData ?? [],
      },
      navbar: {
        appTargetUrls: appTargetUrls,
        navbarBottomColor: teamDocumentData?.navbarBottomColor
          ? teamDocumentData?.navbarBottomColor
          : (orgDocumentData?.org.navbarBottomColor ?? performance.primary),
        navbarTopColor: teamDocumentData?.navbarTopColor
          ? teamDocumentData?.navbarTopColor
          : (orgDocumentData?.org.navbarTopColor ?? black),
        navbarImageUrl: teamDocumentData?.navbarImageUrl
          ? teamDocumentData?.navbarImageUrl
          : (orgDocumentData?.org.navbarImageUrl ?? ''),
        canManageAppAccess: personDocumentData?.canManageAppAccess ?? false,
      },
      reportingUser,
      hubConnected: deriveHubConnected(orgDocumentData),
      hasPermissions,
    };
  }, [
    appTargetUrls,
    auth,
    digitalDisplaysFlag,
    permissions,
    reportingUser,
    orgDocumentData,
    orgDocumentData?.org.name,
    orgDocumentData?.org.navbarTopColor,
    orgDocumentData?.org.navbarBottomColor,
    orgDocumentData?.org.navbarImageUrl,
    orgSettingsData?.settings.enableTeamLibrary,
    orgSettingsData?.settings.requireExerciseMetadataFields,
    orgSettingsData?.settings.showBaseLibrary,
    orgSettingsData?.settings.defaultWeightUnit,
    effectiveTeam,
    setSelectedTeam,
    teamDocumentData?.navbarBottomColor,
    teamDocumentData?.navbarImageUrl,
    teamDocumentData?.navbarTopColor,
    personDocumentData?.canManageAppAccess,
    personDocumentData?._document.login_access?.web?.ams,
    personDocumentData?._document.login_access?.web?.hp,
    teamsData,
    black,
    performance.primary,
    hasPermissions,
  ]);

  // Set reporting user for datadog and gainsight
  // update when any auth or profile related data changes
  useEffect(() => {
    if (auth && auth.type === 'org' && orgDocumentData && reportingUser) {
      // set org user for reporting when profile data is available
      // selectedTeam may be empty for tablet-only users who skip team selection
      setReportingUserForProfile(
        auth,
        orgDocumentData,
        reportingUser,
        effectiveTeam,
      );
    } else if (auth) {
      setReportingUserForAdmin(auth);
    }
  }, [
    auth,
    effectiveTeam,
    teamDocumentData?._document.id,
    orgDocumentData,
    reportingUser,
  ]);

  return (
    <ViewerContext.Provider value={value}>
      <div className="flex flex-col h-screen">
        {environment.VITE_ENV_TYPE === 'il4' && (
          <div className="flex items-center justify-center text-white shrink-0 h-10 bg-[#007315]">
            <Typography variant="h4" color="white">
              CUI//FEDCON
            </Typography>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-hidden">
          {/* SC-4768: while the permissions document is loading, gated UI must
            not mount at all — denied-state consumers (Library/Teams
            punt-to-plan redirects) would otherwise fire against the loading
            document and bounce reloads/deep-links to the plan page. On error
            the query settles and children mount against the fully-denied
            document, which is the intended fail-closed behavior. Disabled
            queries are never loading, so legacy/admin sessions render now. */}
          {myPermissionsLoading ? <MFELoading /> : children}
        </div>
        {environment.VITE_ENV_TYPE === 'il4' && (
          <div className="flex items-center justify-center text-white shrink-0 h-7 bg-[#007315]">
            <Typography variant="body2Semibold" color="white">
              CUI//FEDCON
            </Typography>
          </div>
        )}
      </div>
    </ViewerContext.Provider>
  );
};

export const useViewer = (): ViewerContext => {
  const context = useContext(ViewerContext);
  if (!context) {
    throw new Error('useViewer must be used within a ViewerProvider');
  }
  return context;
};
